// Canonical tag identifiers — the value stored in public.tags.identifier and matched by ingest.
//
//   eddystone:<namespace 20 hex>:<instance 12 hex>     Eddystone-UID (recommended: visible to FMC130, Android, iOS)
//   ibeacon:<uuid 32 hex>:<major>:<minor>              iBeacon (FMC130 and Android; not iOS CoreBluetooth)
//   mac:<12 hex>                                       fallback when only a MAC is known (not visible on iOS)
//   <15 digits>                                        GPS tracker IMEI
//   anything else                                      label code (QR/NFC), kept as given
//
// Hex is lowercase, no separators. Sources: Traccar AVL 385 → beaconN{Namespace,Instance | Uuid,Major,Minor};
// AVL 548 → tagNId (hex: 32 = Eddystone ns+instance, 40 = iBeacon uuid+major+minor) and tagNMac.
// The BLE canonical form is to be confirmed on the phase-0 bench (IMPLEMENTATION_PLAN.md §4 #11).

const HEX = /^[0-9a-f]+$/;
const MAX_LENGTH = 128;

function hex(value: unknown, length: number): string | null {
  if (typeof value !== 'string') return null;
  const v = value.replace(/[\s:-]/g, '').toLowerCase();
  return v.length === length && HEX.test(v) ? v : null;
}

function uint16(value: unknown): number | null {
  const n = typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 0xffff ? n : null;
}

export function eddystoneId(namespace: unknown, instance: unknown): string | null {
  const ns = hex(namespace, 20);
  const inst = hex(instance, 12);
  return ns && inst ? `eddystone:${ns}:${inst}` : null;
}

export function ibeaconId(uuid: unknown, major: unknown, minor: unknown): string | null {
  const u = hex(uuid, 32);
  const ma = uint16(major);
  const mi = uint16(minor);
  return u && ma !== null && mi !== null ? `ibeacon:${u}:${ma}:${mi}` : null;
}

export function macId(mac: unknown): string | null {
  const m = hex(mac, 12);
  return m ? `mac:${m}` : null;
}

/** AVL 548 tagNId: hex dump of the beacon ID parameter. */
export function fromBeaconHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.replace(/\s/g, '').toLowerCase();
  if (!HEX.test(v)) return null;
  if (v.length === 32) return eddystoneId(v.slice(0, 20), v.slice(20));
  if (v.length === 40) {
    return ibeaconId(v.slice(0, 32), parseInt(v.slice(32, 36), 16), parseInt(v.slice(36), 16));
  }
  return null;
}

/**
 * Normalise an identifier typed, scanned or sent by a source. Returns null for input that is empty, too long,
 * or claims a BLE format (eddystone:/ibeacon:/mac:) but does not match it.
 */
export function normalizeTagIdentifier(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || value.length > MAX_LENGTH) return null;

  const prefixed = /^(eddystone|ibeacon|mac)[:\s]\s*(.*)$/i.exec(value);
  if (prefixed) {
    const kind = (prefixed[1] ?? '').toLowerCase();
    const rest = prefixed[2] ?? '';
    if (kind === 'mac') return macId(rest);
    if (kind === 'eddystone') {
      const parts = rest.split(/[\s:]+/);
      return parts.length === 2
        ? eddystoneId(parts[0], parts[1])
        : fromBeaconHex(rest.replace(/[:-]/g, ''));
    }
    const parts = rest.split(/[\s:]+/);
    return parts.length === 3 ? ibeaconId(parts[0], parts[1], parts[2]) : null;
  }

  if (/^\d{15}$/.test(value)) return value; // IMEI
  if (/^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i.test(value)) return macId(value);

  // Unprefixed BLE forms: 32/40 hex (AVL 548), "namespace instance", "uuid major minor".
  const compact = value.replace(/\s+/g, '');
  if (HEX.test(compact.toLowerCase()) && (compact.length === 32 || compact.length === 40)) {
    return fromBeaconHex(compact);
  }
  const words = value.split(/\s+/);
  const [w0, w1, w2] = words;
  if (words.length === 3 && hex(w0, 32)) return ibeaconId(w0, w1, w2);

  return value; // label code
}
